/**
 * CandidatePicker — a bottom sheet listing transactions a receipt might belong
 * to (`GET /api/receipts/{id}/candidates`). Opens whenever `receipt` is
 * non-null (from `ReceiptUploadCard`'s auto-open or `ReceiptQueue`'s
 * "Zuordnen"). The amount filter is manual — it starts empty, so the very
 * first query for a freshly uploaded receipt lands on the same
 * `receiptCandidates(id, undefined)` cache key the upload seeded, and shows
 * instantly. `BelegePage` keys this component on the receipt id so the filter
 * resets on remount instead of via an effect-driven `setState`.
 */
import { useState } from 'react';
import { Search } from 'lucide-react';
import { AmountText } from '../../components/ui/AmountText.tsx';
import { EmptyState } from '../../components/ui/EmptyState.tsx';
import { Input } from '../../components/ui/Input.tsx';
import { Sheet } from '../../components/ui/Sheet.tsx';
import { useOnlineStatus } from '../../context/OnlineStatusContext.tsx';
import { useToast } from '../../context/ToastContext.tsx';
import { formatDate, parseAmountInput } from '../../lib/format.ts';
import { useAttachReceipt, useReceiptCandidates } from '../../lib/hooks/useReceipts.ts';
import { RECEIPT_CONFIDENCE } from '../../lib/labels.ts';
import type { Receipt, ReceiptCandidate, TransferConfidence } from '../../lib/types.ts';

// Confidence pill palette (DESIGN.md §3.11 / §2.2 badges): pastel -100/-800
// light, -950/-300 dark, rounded-full — same recipe as transfers'
// `ConfidenceChip`.
const CONFIDENCE_STYLES: Record<TransferConfidence, string> = {
  high: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  medium: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  low: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
};

export interface CandidatePickerProps {
  receipt: Receipt | null;
  onOpenChange: (open: boolean) => void;
}

export function CandidatePicker({ receipt, onOpenChange }: CandidatePickerProps) {
  const { guardOnline } = useOnlineStatus();
  const { toast } = useToast();
  const attach = useAttachReceipt();
  const [amountRaw, setAmountRaw] = useState('');

  const amountFilter = parseAmountInput(amountRaw) ?? undefined;
  const candidatesQuery = useReceiptCandidates(receipt?.id ?? null, amountFilter);
  const candidates = candidatesQuery.data ?? [];

  function handleAttach(candidate: ReceiptCandidate) {
    if (!receipt) return;
    if (!guardOnline()) return;
    attach.mutate(
      { id: receipt.id, transactionId: candidate.transaction_id },
      {
        onSuccess: () => {
          toast('Beleg zugeordnet', { variant: 'success' });
          onOpenChange(false);
        },
        onError: (error) =>
          toast(error instanceof Error ? error.message : 'Fehler beim Speichern.', {
            variant: 'error',
          }),
      },
    );
  }

  return (
    <Sheet open={receipt !== null} onOpenChange={onOpenChange} title="Buchung zuordnen">
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
            Betrag
          </label>
          <div className="group relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 transition-colors duration-150 group-focus-within:text-sky-600 dark:group-focus-within:text-sky-400"
              aria-hidden
            />
            <Input
              value={amountRaw}
              onChange={(e) => setAmountRaw(e.target.value)}
              placeholder="z. B. 49,90"
              inputMode="decimal"
              className="pl-10"
            />
          </div>
        </div>

        {candidatesQuery.isLoading ? (
          <p className="py-8 text-center text-sm text-slate-400 dark:text-slate-500">Suche …</p>
        ) : candidates.length === 0 ? (
          <EmptyState title="Keine passende Buchung gefunden" />
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800/70">
            {candidates.map((candidate) => (
              <li key={candidate.transaction_id}>
                <button
                  type="button"
                  onClick={() => handleAttach(candidate)}
                  disabled={attach.isPending}
                  className="flex w-full items-center gap-3 rounded py-3 -mx-2 px-2 text-left transition-colors duration-150 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:hover:bg-slate-800/60"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                      {candidate.counterparty_name ?? 'Unbekannt'}
                    </p>
                    <p className="truncate text-xs text-slate-500 dark:text-slate-500">
                      {formatDate(candidate.booking_date)}
                      {candidate.purpose ? ` · ${candidate.purpose}` : ''}
                    </p>
                  </div>
                  <span
                    className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${CONFIDENCE_STYLES[candidate.confidence]}`}
                  >
                    {RECEIPT_CONFIDENCE[candidate.confidence]}
                  </span>
                  <AmountText value={candidate.amount} className="shrink-0 text-right" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Sheet>
  );
}
